/**
 * What the detail view can actually show for a stored original, and what to say
 * when it cannot.
 *
 * The backend serves a document's bytes under its own type only for the types it
 * allow-lists; the browser then decides what to do with them. That leaves three
 * genuinely different renderings and one honest refusal, and the difference is
 * not "what did we upload" but "what will a browser draw":
 *
 * - **pdf** — every browser has a PDF viewer; an `<iframe>` gets real multi-page
 *   rendering, text selection, search and print for nothing.
 * - **image** — PNG and JPEG go straight into an `<img>`.
 * - **text** — plain text is shown as text, not handed to a viewer.
 * - **none** — TIFF above all. It is an accepted *ingestion* type (scanners emit
 *   it constantly) and no mainstream browser renders it, so the preview says so
 *   plainly and offers the download rather than showing a broken image icon.
 */
export type PreviewKind = 'pdf' | 'image' | 'text' | 'none';

/** The bare media type, lower-cased and without parameters. */
export function bareMime(mime: string): string {
  return (mime.split(';', 1)[0] ?? '').trim().toLowerCase();
}

/** How, if at all, the browser can render a document of this type. */
export function previewKindOf(mime: string): PreviewKind {
  switch (bareMime(mime)) {
    case 'application/pdf':
      return 'pdf';
    case 'image/png':
    case 'image/jpeg':
      return 'image';
    case 'text/plain':
      return 'text';
    default:
      return 'none';
  }
}

/** Why there is no preview — specific where being specific helps. */
export function noPreviewReason(mime: string): string {
  const type = bareMime(mime);
  if (type === 'image/tiff') {
    return 'No browser can display TIFF images. The original is stored intact — download it to view the scan.';
  }
  if (type === '') {
    return "This document has no recorded type, so there's no way to preview it. Download the original to view it.";
  }
  return `There's no in-browser preview for ${type}. The original is stored intact — download it to view the file.`;
}

/**
 * Whether a document in this state has bytes to show at all.
 *
 * Everything except `DELETED` does: the original is written to blob storage
 * during ingestion, *before* OCR runs — so a document is previewable the instant
 * it reaches `RECEIVED`, and a `FAILED` one still shows the scan that failed to
 * process, which is exactly what someone diagnosing the failure wants to see.
 * `DELETED` is handled a level up, with its own retention-specific message.
 */
export function hasStoredOriginal(status: string): boolean {
  return status !== 'DELETED';
}

/** The zoom range for the image preview, and the step its buttons move by. */
export const ZOOM_MIN = 0.6;
export const ZOOM_MAX = 3;
export const ZOOM_STEP = 0.2;

/** Clamp a zoom level into range, rounded to avoid float drift in the label. */
export function clampZoom(value: number): number {
  return Number(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value)).toFixed(2));
}
